import { API_BASE_URL } from '../config/api'
import { getAuthHeadersAsync } from '../utils/authUtils'

export interface Feedback {
  id: string
  name: string
  email: string
  userType: string | null
  message: string
  response: string | null
  respondedAt: Date | null
  respondedById: string | null
  respondedBy: {
    id: string
    firstName: string
    lastName: string
    email: string
  } | null
  createdAt: Date
  updatedAt: Date
}

export interface SubmitFeedbackInput {
  name: string
  email: string
  userType?: string
  message: string
}

async function parseJsonResponse(response: Response): Promise<any> {
  const text = await response.text()
  try {
    return text ? JSON.parse(text) : {}
  } catch {
    throw new Error(
      response.ok
        ? 'Invalid response from server'
        : `Server error (${response.status}). Check that the API is deployed and reachable.`
    )
  }
}

export async function submitFeedback(input: SubmitFeedbackInput): Promise<Feedback> {
  const response = await fetch(`${API_BASE_URL}/api/feedback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  })

  const data = await parseJsonResponse(response)

  if (!response.ok) {
    throw new Error(data.error || 'Failed to submit feedback')
  }

  return data.data
}

export async function getFeedback(): Promise<Feedback[]> {
  const headers = {
    'Content-Type': 'application/json',
    ...(await getAuthHeadersAsync()),
  }

  const response = await fetch(`${API_BASE_URL}/api/feedback`, {
    method: 'GET',
    headers,
  })

  const data = await parseJsonResponse(response)

  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch feedback')
  }

  return data.data ?? []
}

export async function respondToFeedback(id: string, responseText: string): Promise<Feedback> {
  const headers = {
    'Content-Type': 'application/json',
    ...(await getAuthHeadersAsync()),
  }

  const res = await fetch(`${API_BASE_URL}/api/feedback/${id}/respond`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ response: responseText }),
  })

  const data = await parseJsonResponse(res)

  if (!res.ok) {
    throw new Error(data.error || 'Failed to respond to feedback')
  }

  return data.data
}

export async function deleteFeedback(id: string): Promise<void> {
  const headers = {
    'Content-Type': 'application/json',
    ...(await getAuthHeadersAsync()),
  }

  const response = await fetch(`${API_BASE_URL}/api/feedback/${id}`, {
    method: 'DELETE',
    headers,
  })

  const data = await parseJsonResponse(response)

  if (!response.ok) {
    throw new Error(data.error || 'Failed to delete feedback')
  }
}
